/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { RedirectHelper } from '../../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { Organization } from '../../../shared/models';
import { MaintenanceService } from '../../../shared/services/maintenance/maintenance.service';
import { AUTH_SERVICE, BasicAuthService } from '../../services/basic-auth.service';
import { UserAuthState } from '../../store/user-auth.state';

@Component({
  selector: 'ptl-invitation-join',
  templateUrl: './invitation-join.component.html',
  styleUrls: ['./invitation-join.component.scss'],
})
export class InvitationJoinComponent implements OnDestroy {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @Output() backClicked = new EventEmitter<void>();

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  invitationId: string;
  invitationToken: string;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;
  joinReady = false;
  showMaintenanceOverlay: boolean;

  private organizationDataSubscription: Subscription;
  private validatedEmail: string;
  private organizationDomain: string;

  constructor(
    store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    private captchaV3Service: ReCaptchaV3Service,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private maintenanceService: MaintenanceService,
  ) {
    this.showMaintenanceOverlay = this.maintenanceService.showMaintenanceMessage();
    const params = this.router.parseUrl(this.router.url).queryParamMap;
    this.invitationToken = params.get('token');
    this.invitationId = this.route.snapshot.paramMap.get('invitationId');
    this.organizationDataSubscription = this.organizationData$.pipe(filter((data) => !!data)).subscribe((data) => {
      this.organizationDomain = data.domain;
      if (this.invitationId) {
        this.authService.validateInvitationToken(this.invitationId, this.invitationToken).subscribe(({ isSuccess, value }) => {
          if (isSuccess) {
            this.validatedEmail = value.email;
            this.joinReady = true;
          }
        });
      }
    });
  }

  ngOnDestroy() {
    this.organizationDataSubscription?.unsubscribe();
  }

  onJoinButtonClick() {
    this.captchaElem.execute();
  }

  proceedJoin(captchaResponseV2: string | null) {
    if (!captchaResponseV2) return;

    const request = {
      email: this.validatedEmail?.trim(),
      organizationDomain: this.organizationDomain,
      captchaResponseV3: null,
      captchaResponseV2: captchaResponseV2,
    };

    this.captchaV3Service.execute('invitationUserJoin').subscribe((captchaResponseV3: string) => {
      request.captchaResponseV3 = captchaResponseV3;
      this.authService.joinOrganizationWithInvitationToken(request, this.invitationId, this.invitationToken).subscribe((response) => {
        if (response.isSuccess) {
          this.snackBar.open(this.translocoService.translate('translations.invitations.message.success.join'), '', {
            duration: 2000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          RedirectHelper.redirectByUrl(this.ngZone, this.router, this.route, '/signin');
        } else {
          this.snackBar.open(this.translocoService.translate('translations.invitations.message.error.join'), '', {
            duration: 2000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
        this.captchaElem.reset();
        this.cdr.detectChanges();
      });
    });
  }
}
